// "Connected medical records" card — the EHR (SMART-on-FHIR) connect surface
// (SPEC-ADDENDUM §A5b). Shows the Subject's link status, a Connect button that
// hands off to the EHR's consent screen, and a Disconnect (consent withdrawal).
//
// Connect calls POST /authorize and sends the browser to the returned EHR URL;
// the EHR later redirects to the public /callback, which 303s back to
// /?ehr=<result> (handled by useEhrRedirectToast in Layout). Status + disconnect
// work in any backend mode; the authorize hand-off only works once the backend
// is in epic mode with a registered client id — until then the button surfaces a
// clear "not enabled yet" message rather than failing opaquely.

import { useState } from "react";

import { ApiError, ehr, subjects } from "../lib/api";
import type { EhrConnectionStatus, SubjectSummary } from "../lib/types";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "./Toast";

interface SubjectConnection {
  subject: SubjectSummary | null;
  connection: EhrConnectionStatus | null;
}

const STATUS_BADGE: Record<string, string> = {
  active: "badge-green",
  pending: "badge-yellow",
  revoked: "badge-gray",
  error: "badge-red",
  not_connected: "badge-gray",
};

function fmt(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function EhrConnectCard(): JSX.Element {
  const [busy, setBusy] = useState(false);

  const state = useAsync<SubjectConnection>(async () => {
    const list = await subjects.list();
    const subject = list.subjects.find((s) => s.is_default) ?? list.subjects[0] ?? null;
    if (!subject) return { subject: null, connection: null };
    const connection = await ehr.connectionStatus(subject.id);
    return { subject, connection };
  }, []);

  async function onConnect(subjectId: number): Promise<void> {
    setBusy(true);
    try {
      const resp = await ehr.authorize(subjectId);
      // Hand off to the EHR's consent screen. The round-trip returns to
      // /callback → /?ehr=<result>.
      window.location.href = resp.authorization_url;
    } catch (err) {
      if (err instanceof ApiError && err.code === "EHR_NOT_CONFIGURED") {
        pushToast("warning", "Connecting records isn't enabled yet — Epic setup is still pending.");
      } else if (err instanceof ApiError && err.code === "EHR_ROUTE_DISABLED") {
        pushToast("info", "Records connect runs in live (epic) mode; this environment is in mock mode.");
      } else {
        pushToast("error", err instanceof ApiError ? err.message : "Could not start the connection.");
      }
      setBusy(false);
    }
  }

  async function onDisconnect(subjectId: number): Promise<void> {
    setBusy(true);
    try {
      await ehr.disconnect(subjectId);
      pushToast("success", "Medical records disconnected. Stored access was revoked.");
      state.refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Could not disconnect.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-6">
      <h2 className="mb-1 text-lg font-medium text-slate-800">Connected medical records</h2>
      <p className="mb-4 text-sm text-slate-500">
        Link a patient portal (Epic / MyChart) to pull records automatically. We store only an
        encrypted access token; you can disconnect at any time.
      </p>

      {state.loading && <p className="text-sm text-slate-500">Loading…</p>}
      {state.error && (
        <p className="text-sm text-red-600">Couldn't load connection status: {state.error.message}</p>
      )}

      {!state.loading && state.data && !state.data.subject && (
        <p className="text-sm text-slate-500">No Subject on this account yet.</p>
      )}

      {!state.loading && state.data?.subject && state.data.connection && (
        <EhrConnectionBody
          subjectId={state.data.subject.id}
          connection={state.data.connection}
          busy={busy}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
        />
      )}
    </section>
  );
}

function EhrConnectionBody({
  subjectId,
  connection,
  busy,
  onConnect,
  onDisconnect,
}: {
  subjectId: number;
  connection: EhrConnectionStatus;
  busy: boolean;
  onConnect: (id: number) => void;
  onDisconnect: (id: number) => void;
}): JSX.Element {
  const isActive = connection.status === "active";
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className={STATUS_BADGE[connection.status] ?? "badge-gray"}>{connection.status}</span>
        {connection.provider && isActive && (
          <span className="text-sm text-slate-600">via {connection.provider}</span>
        )}
      </div>

      {isActive && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Patient</dt>
            <dd className="text-slate-900">{connection.fhir_patient_id ?? "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Last pull</dt>
            <dd className="text-slate-900">{fmt(connection.last_pull_at)}</dd>
          </div>
        </dl>
      )}

      <div className="flex gap-3">
        {isActive ? (
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={() => onDisconnect(subjectId)}
          >
            Disconnect
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onConnect(subjectId)}
          >
            {connection.status === "revoked" ? "Reconnect medical records" : "Connect medical records"}
          </button>
        )}
      </div>
    </div>
  );
}
